import {inject, injectable} from "inversify";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {LiveMatchHandlerInterface} from "@/modules/match/interfaces";
import LiveMatchEvaluator from "@/modules/match/services/LiveMatchEvaluator";
import {HomeAwayType, LiveIncidentType, TickType} from "@/modules/match/types";
import {Player} from "@/modules/team/entity";
import {LineupEnum, PositionEnum} from "@/modules/base/enums";
import {Collection} from "collect.js";
import chance from "@/modules/base/services/chance";
import {CardIncident, Match, SubstitutionIncident} from "@/modules/match/entity";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";
import {FormationEnum} from "@/modules/team/enums";
import {BeforeMatchChangePossessionEvent} from "@/modules/match/events/BeforeMatchChangePossessionEvent";
import {AfterMatchChangePossessionEvent} from "@/modules/match/events/AfterMatchChangePossessionEvent";
import {BeforeMatchProgressBallPositionEvent} from "@/modules/match/events/BeforeMatchProgressBallPositionEvent";
import {AfterMatchProgressBallPositionEvent} from "@/modules/match/events/AfterMatchProgressBallPositionEvent";
import {BeforeMatchLaneCalculatedEvent} from "@/modules/match/events/BeforeMatchLaneCalculatedEvent";
import {AfterMatchLaneCalculatedEvent} from "@/modules/match/events/AfterMatchLaneCalculatedEvent";
import LaneCalculator from "@/modules/match/services/live-match-handlers/lane-based-goals-handler/LaneCalculator";
import {CardEnum} from "@/modules/match/enums";

export type LaneType = { players: Collection<Player>, effectiveSkill: number };
export type LanesType = { home: LaneType[], away: LaneType[] };

@injectable()
export default class LaneBasedGoalsHandler implements LiveMatchHandlerInterface {
  private lanes: LanesType = {home: [], away: []};
  public ballLanePosition = 2;
  public possession: HomeAwayType = 'home';
  private goalKick = false;
  private liveMatchEvaluator: LiveMatchEvaluator;
  private match: Match;

  private suspendedPlayerId: number[] = [];

  // attacking team get the advantage.
  // - higher value will result in more chances
  static readonly attackingAdvantage = 25;
  // probability of player shooting on goal when on last / penultimate lane.
  // - higher value will result in more chances
  static readonly shootingChance = {distance: 30, close: 60};
  // chance of the goalkeeper to save the ball in case of same skill.
  // - higher value will result in less goals
  static readonly gkChance = 85;
  // factor on which the skill difference between player with chance and goalkeeper is taken into account
  // - higher factor result in less significance of skill difference
  static readonly gkPlayerSkillEffect = 2;
  // when doing goal kick this is the advantage for maintaining the possession in %.
  // - higher value will give advantage to weak teams
  static readonly goalKickAdvantage = 10;

  static readonly lanesCount = 5;

  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(EffectiveLineupSkillCalculator) private effectiveLineupSkillCalculator: EffectiveLineupSkillCalculator,
    @inject(LaneCalculator) private laneCalculator: LaneCalculator,
  ) {
  }

  priority() {
    return 0;
  };

  async onFinish(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType): Promise<void> {
  }

  async onIncident(liveMatchEvaluator: LiveMatchEvaluator, tick: LiveIncidentType): Promise<void> {
    if (tick.incident instanceof SubstitutionIncident) {
      await this.calculateLanes(tick);
    }

    if (tick.incident instanceof CardIncident && [CardEnum.YELLOW_RED, CardEnum.RED].includes(tick.incident.card)) {
      this.suspendedPlayerId.push(tick.incident.playerId!);
    }
  }

  async onTick(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType): Promise<void> {
    this.liveMatchEvaluator = liveMatchEvaluator;
    this.match = tick.match;

    this.log('minute', tick.minute);

    await this.calculateLanes(tick);

    this.possession === 'home'
      ? tick.match.homePossession = tick.match.homePossession! + 1
      : tick.match.awayPossession = tick.match.awayPossession! + 1;

    if (await this.processGoalKick(tick)) {
      return;
    }

    if (await this.processShooting(tick)) {
      return;
    }

    const oppLaneSkill = this.getLaneSkill(this.possessionOpp);
    if (oppLaneSkill === 0) {
      await this.changePossession();
      return;
    }

    const likelihood = this.getLikelihood(
      this.getLaneSkill(this.possession) * (1 + LaneBasedGoalsHandler.attackingAdvantage / 100),
      oppLaneSkill
    );

    this.log('lane chance to win', likelihood);

    if (chance.bool({likelihood})) {
      await this.progressBallPosition();
    } else {
      await this.changePossession();
    }
  }

  private processGoalKick(tick: TickType) {
    if (this.goalKick) {
      this.log('goal kick');

      this.goalKick = false;
      this.ballLanePosition = 2;

      const adv = LaneBasedGoalsHandler.goalKickAdvantage / 100;
      const likelihood = this.getLikelihood(adv * this.getLaneSkill(this.possession), this.getLaneSkill(this.possessionOpp));

      this.possession = chance.bool({likelihood}) ? 'home' : 'away';
    }

    return false;
  }

  private async processShooting(tick: TickType) {
    const isInShootingRange = this.possession === 'home' ? this.ballLanePosition >= 3 : this.ballLanePosition <= 1;

    if (!isInShootingRange) {
      return false;
    }

    const isDistance = this.possession === 'home' ? this.ballLanePosition === 3 : this.ballLanePosition === 1;
    const likelihood = isDistance ? LaneBasedGoalsHandler.shootingChance.distance : LaneBasedGoalsHandler.shootingChance.close;

    if (!chance.bool({likelihood})) {
      this.log('do not shoot');

      return false;
    }

    const gk = this.getGoalKeeper(tick);
    const player = this.getLanePlayer(tick);

    if (!player) {
      this.log('no player available on lane');

      this.goalKick = true;

      return true;
    }

    let team;
    let teamOpp;

    if (this.possession === 'home') {
      team = tick.match.homeTeam;
      teamOpp = tick.match.awayTeam;
    } else {
      team = tick.match.awayTeam;
      teamOpp = tick.match.homeTeam;
    }

    const gkSkill = (gk ? this.getPlayerSkill(gk)! : 0) / LaneBasedGoalsHandler.gkPlayerSkillEffect;
    const playerSkill = this.getPlayerSkill(player)! / LaneBasedGoalsHandler.gkPlayerSkillEffect;
    const likelihoodScoreGoal = 100 - this.getLikelihood(gkSkill, playerSkill * (100 - LaneBasedGoalsHandler.gkChance) / 100);

    if (chance.bool({likelihood: likelihoodScoreGoal})) {
      this.log('shoot: hit', {player, likelihoodScoreGoal, gkSkill, playerSkill});

      this.addChance(tick);
      this.addGoal(tick);

      const incident = await this.liveMatchEvaluator.createGoalIncident(tick.match, team, teamOpp, tick.minute, player);

      await this.liveMatchEvaluator.triggerIncident(
        Object.assign({}, tick, {incident}),
      );

      await this.changePossession();
      this.ballLanePosition = 2;
    } else {
      this.log('shoot: miss', {player, likelihoodScoreGoal, gkSkill, playerSkill});

      this.addChance(tick);

      const incident = await this.liveMatchEvaluator.createMissedGoalIncident(tick.match, team, player, tick.minute);

      await this.liveMatchEvaluator.triggerIncident(
        Object.assign({}, tick, {incident}),
      );

      this.goalKick = true;
    }

    return true;
  }

  private addGoal(tick: TickType) {
    this.possession === 'home'
      ? tick.match.homeGoals = tick.match.homeGoals! + 1
      : tick.match.awayGoals = tick.match.awayGoals! + 1;
  }

  private addChance(tick: TickType) {
    this.possession === 'home'
      ? tick.match.homeChances = tick.match.homeChances! + 1
      : tick.match.awayChances = tick.match.awayChances! + 1;
  }

  private getGoalKeeper(tick: TickType) {
    return (this.possession === 'home' ? tick.awayPlayers : tick.homePlayers)
      .find(p => p.lineup === 1);
  }

  private formationToPosition(formation: FormationEnum, lineup: LineupEnum): PositionEnum {
    const definition = this.configRepository.get('formations.all')[formation];

    return definition[lineup].position;
  }

  private async calculateLanes(tick: TickType) {
    this.log('calculate lanes', this.lanes, this);

    await this.liveMatchEvaluator.em.emitAsync(new BeforeMatchLaneCalculatedEvent(this.match, this.lanes));

    this.lanes.home = this.laneCalculator.calculate(tick.match.homeTeam, this.suspendedPlayerId);
    this.lanes.away = this.laneCalculator.calculate(tick.match.awayTeam, this.suspendedPlayerId);

    await this.liveMatchEvaluator.em.emitAsync(new AfterMatchLaneCalculatedEvent(this.match, this.lanes));
  }

  async changePossession() {
    this.log('switch possession');

    await this.liveMatchEvaluator.em.emitAsync(new BeforeMatchChangePossessionEvent(this.match, this.possession));

    this.possession = this.possessionOpp;

    await this.liveMatchEvaluator.em.emitAsync(new AfterMatchChangePossessionEvent(this.match, this.possession));
  }

  async progressBallPosition() {
    this.log('progress ball position', this.ballLanePosition);

    await this.liveMatchEvaluator.em.emitAsync(new BeforeMatchProgressBallPositionEvent(this.match, this.ballLanePosition));

    this.ballLanePosition += this.possession === 'home' ? 1 : -1;
    this.ballLanePosition = Math.min(
      LaneBasedGoalsHandler.lanesCount - 1,
      Math.max(0, this.ballLanePosition)
    );

    await this.liveMatchEvaluator.em.emitAsync(new AfterMatchProgressBallPositionEvent(this.match, this.ballLanePosition));
  }

  private getLanePlayer(tick: TickType) {
    const laneIndex = this.getLaneIndex(this.possession);
    const lanePlayers = this.lanes[this.possession][laneIndex].players;

    if (lanePlayers.isEmpty()) {
      return null;
    }

    return chance.pickone<Player>(this.lanes[this.possession][laneIndex].players.toArray());
  }

  getLaneIndex(homeAway: HomeAwayType): number {
    return homeAway === 'home'
      ? this.ballLanePosition
      : (LaneBasedGoalsHandler.lanesCount - 1) - this.ballLanePosition;
  }

  getPlayerSkill(player: Player) {
    return this.effectiveLineupSkillCalculator.calculateLinedupSkillPlayerByTeam(
      player.teamId === this.match.homeTeam.id ? this.match.homeTeam : this.match.awayTeam,
      player
    );
  }

  getLaneSkill(homeAway: HomeAwayType): number {
    return this.lanes[homeAway][this.getLaneIndex(homeAway)].effectiveSkill;
  }

  get possessionOpp(): HomeAwayType {
    return this.possession === 'home' ? 'away' : 'home';
  }

  getLikelihood(x: number, y: number) {
    return Math.min(100, x / (x + y) * 100);
  }

  log(...args: any[]): void {
    console.log('Match', this.possession, this.ballLanePosition, ...args);
  }
}
